<template>
  <div v-if="visible" class="kpo-backdrop">
    <div class="kpo-card">
      <h2>Inserisci il contante</h2>
      <div class="kpo-amounts">
        <div class="kpo-row"><span>Richiesto</span><b>{{ eur(requested) }}</b></div>
        <div class="kpo-row"><span>Inserito</span><b>{{ eur(inserted) }}</b></div>
        <div class="kpo-row kpo-remaining" v-if="remaining > 0"><span>Mancano</span><b>{{ eur(remaining) }}</b></div>
      </div>
      <div class="kpo-timer" :class="{ warn: displaySeconds <= 30 }">{{ displaySeconds }}s</div>
      <div class="kpo-hint">Inserisci contante entro il tempo, altrimenti l'operazione viene annullata.</div>
      <!-- Annullo possibile SOLO finché non è entrato contante. -->
      <button v-if="inserted === 0" class="kpo-cancel" :disabled="cancelling" @click="cancelPayment">
        {{ cancelling ? 'Annullamento…' : 'Annulla' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { hasKioskBridge } from '@/config/kiosk'
import { kioskSession } from '@/config/kioskSession'

const visible = ref(false)
const requested = ref(0)
const inserted = ref(0)
const displaySeconds = ref(120)
const cancelling = ref(false)

// Il checkout e il timeout di inattività devono sapere che il Cashmatic sta incassando.
watch(visible, (v) => { kioskSession.paymentActive = v }, { immediate: true })

let unsub = null
let hideTimer = null
let tick = null

// Istante in cui il tempo scade, non "quanti secondi mancano": da un istante
// fisso il conto alla rovescia scende sempre, qualunque sia la cadenza con
// cui arrivano gli aggiornamenti del gateway.
let deadlineAt = 0

// Il gateway riporta i secondi con la cadenza irregolare del suo polling.
// Sotto questa soglia la differenza e' jitter e va ignorata, altrimenti il
// numero rimbalza avanti e indietro di un secondo; sopra, e' il timer che il
// gateway ha davvero rimesso a nuovo (succede a ogni banconota inserita).
const RESYNC_THRESHOLD_S = 2

function secondsToDeadline () {
  return Math.max(0, Math.ceil((deadlineAt - Date.now()) / 1000))
}

const remaining = computed(() => Math.max(0, requested.value - inserted.value))
function eur (cents) { return ((Number(cents) || 0) / 100).toFixed(2) + ' €' }

async function cancelPayment () {
  // Consentito solo finché non è entrato contante (il bottone è nascosto dopo).
  if (inserted.value > 0 || cancelling.value) return
  cancelling.value = true
  try {
    if (hasKioskBridge()) await window.kioskPay.cancel()
  } catch (_) {
    // best-effort: il polling del gateway rileverà comunque l'annullo
  }
}

function onProgress (p) {
  if (!p) return
  if (p.operation === 'payment') {
    requested.value = p.requested || requested.value
    inserted.value = p.inserted || 0
    if (typeof p.secondsLeft === 'number') {
      // Prima scadenza, oppure il gateway si e' discostato troppo dalla nostra
      // stima: riallineiamo. Negli altri casi lasciamo scorrere il conto
      // locale, che e' gia' allineato e non ha scatti.
      if (!deadlineAt || Math.abs(p.secondsLeft - secondsToDeadline()) >= RESYNC_THRESHOLD_S) {
        deadlineAt = Date.now() + p.secondsLeft * 1000
        displaySeconds.value = p.secondsLeft
      }
    }
    visible.value = true
    // Se il polling si interrompe (fine flusso), nascondi dopo poco.
    if (hideTimer) clearTimeout(hideTimer)
    hideTimer = setTimeout(() => { visible.value = false }, 4000)
  } else if (p.operation === 'idle') {
    visible.value = false
    cancelling.value = false
    deadlineAt = 0
  }
}

onMounted(() => {
  if (hasKioskBridge()) unsub = window.kioskPay.onProgress(onProgress)
  // Si controlla piu' volte al secondo, ma il numero cambia solo quando cambia
  // davvero: il valore viene letto dalla scadenza, non decrementato a mano, e
  // il passaggio di secondo cade quando deve invece che al ritmo del timer.
  tick = setInterval(() => {
    if (!visible.value || !deadlineAt) return
    const next = secondsToDeadline()
    if (next !== displaySeconds.value) displaySeconds.value = next
  }, 250)
})

onBeforeUnmount(() => {
  if (unsub) unsub()
  if (hideTimer) clearTimeout(hideTimer)
  if (tick) clearInterval(tick)
  kioskSession.paymentActive = false
})
</script>

<style scoped>
.kpo-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.75);
  font-family: 'Montserrat', sans-serif;
}
.kpo-card {
  background: #2e348c;
  color: #fff;
  border-radius: 24px;
  padding: 3rem 3.5rem;
  text-align: center;
  min-width: 360px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.5);
}
.kpo-card h2 { font-size: 2rem; font-weight: 700; margin: 0 0 1.5rem; }
.kpo-amounts { font-size: 1.4rem; margin-bottom: 1.5rem; }
.kpo-row { display: flex; justify-content: space-between; gap: 2rem; padding: 0.35rem 0; }
.kpo-row span { opacity: 0.8; }
.kpo-remaining b { color: #ffd54a; }
.kpo-timer {
  font-size: 4rem;
  font-weight: 700;
  margin: 1rem 0 0.5rem;
  color: #6be585;
}
.kpo-timer.warn { color: #ff6b6b; }
.kpo-hint { font-size: 1rem; opacity: 0.7; }
.kpo-cancel {
  margin-top: 1.5rem;
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 12px 32px;
  font-size: 1.1rem;
  font-family: 'Montserrat', sans-serif;
  cursor: pointer;
}
.kpo-cancel:active { background: rgba(255, 255, 255, 0.28); }
.kpo-cancel:disabled { opacity: 0.5; cursor: default; }
</style>
